import { useEffect, useState } from 'react';
import { UserPlus, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * InviteFriendButton — voce della Sidebar che apre un mini-modale per inserire
 * l'email di un amico e inviargli un invito (magic-link Supabase).
 *
 * - Stati: idle | sending | success | error
 * - Backend: POST /api/invites {email}
 * - Risposta: { ok, mode: 'invite' | 'magiclink', messaggio }
 * - Quota: GET /api/invites/quota → {used, limit, remaining}.
 *   Caricata all'apertura del modale; mostrata come pill nel header e
 *   refetch dopo ogni invio riuscito (così l'utente vede il decremento).
 *
 * Usato in `Sidebar.jsx` (footer, sopra il pulsante "Esci").
 */
export default function InviteFriendButton({ collapsed = false }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | error
  const [message, setMessage] = useState('');
  const [quota, setQuota] = useState(null); // {used, limit, remaining} | null

  const fetchQuota = async () => {
    try {
      const { data } = await api.get('/invites/quota', { silent: true });
      setQuota(data);
    } catch {
      setQuota(null);
    }
  };

  useEffect(() => {
    if (open) fetchQuota();
  }, [open]);

  const reset = () => {
    setEmail('');
    setState('idle');
    setMessage('');
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const send = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setState('error');
      setMessage('Email non valida');
      return;
    }
    setState('sending');
    setMessage('');
    try {
      const { data } = await api.post('/invites', { email });
      setState('success');
      setMessage(data?.messaggio || `Invito inviato a ${email}.`);
      fetchQuota();
    } catch (e) {
      setState('error');
      setMessage(e?.response?.data?.detail || 'Errore invio invito');
      // 429? la quota è cambiata
      if (e?.response?.status === 429) fetchQuota();
    }
  };

  const isZero = quota && quota.remaining === 0;
  const isLow = quota && quota.remaining > 0 && quota.remaining <= 2;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group w-full flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-linesoft text-soft hover:text-ink"
        data-testid="sidebar-invite-friend"
        aria-label="Invita un amico"
        title="Invita un amico"
      >
        <span className="shrink-0 inline-flex">
          <UserPlus size={16} strokeWidth={1.5} />
        </span>
        {!collapsed && (
          <span className="flex-1 truncate font-mono text-[11px] uppercase tracking-[0.18em]">
            Invita un amico
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={close}
          data-testid="invite-friend-modal-backdrop"
        >
          <div
            className="w-full max-w-md mx-4 rounded-md bg-paper shadow-xl border border-line"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Invita un amico su Quantum Planner"
            data-testid="invite-friend-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-linesoft">
              <div className="flex items-center gap-3">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-soft">
                  Invita un amico
                </h2>
                {quota && quota.used !== null && (
                  <span
                    className={[
                      'font-mono text-[10px] uppercase tracking-[0.16em] rounded-full px-2 py-0.5 border tabular-nums',
                      isZero
                        ? 'bg-risk/10 text-risk border-risk/20 animate-pulse'
                        : isLow
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-ok/10 text-ok border-ok/20',
                    ].join(' ')}
                    title={`${quota.used}/${quota.limit} inviti usati nelle ultime 24h`}
                    data-testid="invite-friend-quota"
                  >
                    {quota.remaining}/{quota.limit} rimasti
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={close}
                className="text-mute hover:text-ink"
                data-testid="invite-friend-modal-close"
                aria-label="Chiudi"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              <p className="text-sm text-soft">
                Inserisci l&apos;email del tuo amico: Supabase gli invierà un link di accesso
                magico. Quando lo apre, atterra direttamente sulla schermata di login.
              </p>

              <div>
                <label
                  htmlFor="invite-email"
                  className="block font-mono text-[10px] uppercase tracking-[0.18em] text-mute mb-1.5"
                >
                  Email
                </label>
                <input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !isZero && send()}
                  placeholder="amico@esempio.com"
                  className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-ink"
                  disabled={state === 'sending' || state === 'success' || isZero}
                  autoFocus
                  data-testid="invite-friend-email-input"
                />
              </div>

              {isZero && state !== 'success' && (
                <div
                  className="flex items-start gap-2 text-sm text-risk bg-risk/10 border border-risk/20 rounded px-3 py-2"
                  data-testid="invite-friend-quota-exhausted"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    Hai raggiunto il limite di <strong>{quota.limit} inviti</strong> nelle
                    ultime 24h. Riprova domani.
                  </span>
                </div>
              )}

              {state === 'success' && (
                <div
                  className="flex items-start gap-2 text-sm text-ok bg-ok/10 border border-ok/20 rounded px-3 py-2"
                  data-testid="invite-friend-success"
                >
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                  <span>{message}</span>
                </div>
              )}

              {state === 'error' && (
                <div
                  className="flex items-start gap-2 text-sm text-risk bg-risk/10 border border-risk/20 rounded px-3 py-2"
                  data-testid="invite-friend-error"
                >
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{message}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-linesoft bg-canvas rounded-b-md">
              <button
                type="button"
                onClick={close}
                className="font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 text-soft hover:text-ink"
                data-testid="invite-friend-cancel"
              >
                {state === 'success' ? 'Chiudi' : 'Annulla'}
              </button>
              {state !== 'success' && (
                <button
                  type="button"
                  onClick={send}
                  disabled={state === 'sending' || !email || isZero}
                  className="font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 bg-ink text-white rounded hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="invite-friend-send"
                >
                  {state === 'sending' && <Loader2 size={12} className="animate-spin" />}
                  {state === 'sending' ? 'Invio…' : 'Invia invito'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
